import type { FieldChange, FormProblem, FormProblems } from '@jfh/intake-shared';
import { Modal } from '../components/Modal.js';
import { Icon } from '../components/Icon.js';

/** Where a review item takes you: a question, or the Form settings tab. */
export type GoTo = (fieldId: string | null, tab?: 'settings') => void;

export interface PublishReviewProps {
  open: boolean;
  onClose: () => void;
  problems: FormProblems;
  changes: FieldChange[];
  /** The live version number, or null if the form has never been published. */
  liveVersion: number | null;
  draftVersion: number;
  publishing: boolean;
  onPublish: () => void;
  onGoTo: GoTo;
}

const KIND_ICON: Record<FieldChange['kind'], string> = {
  add: 'plus',
  remove: 'trash',
  edit: 'pencil',
  move: 'drag',
};

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: 'var(--text-3)',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        margin: '18px 0 8px',
      }}
    >
      {children}
    </div>
  );
}

function ProblemRow({ p, tone, onGoTo }: { p: FormProblem; tone: 'error' | 'warn'; onGoTo: GoTo }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '9px 11px',
        borderRadius: 8,
        marginBottom: 6,
        background: tone === 'error' ? 'var(--danger-100)' : 'var(--warning-100)',
        fontSize: 13,
      }}
    >
      <Icon name={tone === 'error' ? 'x' : 'flag'} size={14} />
      <span style={{ flex: 1 }}>{p.text}</span>
      <button className="btn sm" onClick={() => onGoTo(p.fieldId, p.tab)}>
        {p.fix}
      </button>
    </div>
  );
}

/**
 * Review & publish (F9 P1): what would break, what's changed since the live
 * version, then publish. Must-fix problems block publishing.
 */
export function PublishReview({
  open,
  onClose,
  problems,
  changes,
  liveVersion,
  draftVersion,
  publishing,
  onPublish,
  onGoTo,
}: PublishReviewProps) {
  const blocked = problems.errors.length > 0;
  const nothingNew = liveVersion !== null && changes.length === 0;

  return (
    <Modal open={open} onClose={onClose} width={620}>
      <div style={{ padding: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 17 }}>Review &amp; publish</div>
            <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginTop: 2 }}>
              Publishing makes this draft the form applicants see, straight away.
            </div>
          </div>
          <button className="icon-btn" onClick={onClose} title="Close">
            <Icon name="x" size={16} />
          </button>
        </div>

        <SectionTitle>Checks</SectionTitle>
        {problems.errors.length === 0 && problems.warnings.length === 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-2)' }}>
            <Icon name="check" size={14} /> No problems found.
          </div>
        )}
        {problems.errors.length > 0 && (
          <div style={{ fontSize: 12.5, fontWeight: 700, margin: '0 0 6px' }}>
            Must fix before publishing ({problems.errors.length})
          </div>
        )}
        {problems.errors.map((p, i) => (
          <ProblemRow key={`e${i}`} p={p} tone="error" onGoTo={onGoTo} />
        ))}
        {problems.warnings.length > 0 && (
          <div style={{ fontSize: 12.5, fontWeight: 700, margin: '10px 0 6px' }}>
            Worth a look ({problems.warnings.length})
          </div>
        )}
        {problems.warnings.map((p, i) => (
          <ProblemRow key={`w${i}`} p={p} tone="warn" onGoTo={onGoTo} />
        ))}

        <SectionTitle>
          {liveVersion === null ? 'What applicants will see' : `What's changed since v${liveVersion}`}
        </SectionTitle>
        {liveVersion === null ? (
          <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
            This form hasn't been published yet, so everything in the draft is new.
          </div>
        ) : nothingNew ? (
          <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
            No question changes since v{liveVersion}.
          </div>
        ) : (
          <div>
            {changes.map((c, i) => (
              <button
                key={i}
                onClick={() => c.fieldId && onGoTo(c.fieldId)}
                disabled={!c.fieldId}
                style={{
                  display: 'flex',
                  gap: 10,
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 10px',
                  border: 'none',
                  borderBottom: '1px solid var(--border)',
                  background: 'transparent',
                  color: 'var(--text-1)',
                  cursor: c.fieldId ? 'pointer' : 'default',
                }}
              >
                <span style={{ color: 'var(--text-3)', paddingTop: 2 }}>
                  <Icon name={KIND_ICON[c.kind]} size={14} />
                </span>
                <span>
                  <span style={{ display: 'block', fontSize: 13, fontWeight: 600 }}>{c.text}</span>
                  <span style={{ display: 'block', fontSize: 12, color: 'var(--text-2)' }}>{c.detail}</span>
                </span>
              </button>
            ))}
          </div>
        )}
        <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 10 }}>
          Form settings aren't listed here: they went live when you saved them.
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
          <button className="btn" onClick={onClose}>
            Keep editing
          </button>
          <button
            className="btn primary"
            onClick={onPublish}
            disabled={blocked || publishing || nothingNew}
            title={blocked ? 'Fix the problems above first' : nothingNew ? 'Nothing new to publish' : undefined}
          >
            <Icon name="bolt" size={15} />
            {publishing ? 'Publishing…' : `Publish v${draftVersion}`}
          </button>
        </div>
      </div>
    </Modal>
  );
}
